var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback } from 'react';
import MultiSelect from './MultiSelect';
import { useUXPContext } from '@core/context/useUXPContext';
import { executeConfig } from '@core/context/useExecuteRequest';
// Module-level cache — persists across component mounts within the same page session.
// Keyed by apiKey so it stays isolated per user/account.
const _cache = {};
/**
 * @export
 * A multi-select input for selecting App:Role pairs.
 * Options are loaded on demand (10 per page) and cached per session.
 * Supports search filtering by typing in the dropdown.
 *
 * @example
 * ```
 * <AppRoleInput
 *   value={appRoles}
 *   onChange={setAppRoles}
 * />
 * ```
 */
const AppRoleInput = ({ value, onChange, disabled, readOnly, }) => {
    const uxpContext = useUXPContext();
    const cacheKey = uxpContext.apiKey || 'default';
    const ensureCache = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (_cache[cacheKey])
            return _cache[cacheKey];
        const apps = uxpContext.enabledApps || [];
        const results = yield Promise.all(apps.map((app) => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield executeConfig(uxpContext, {
                type: 'service',
                app: 'Auth',
                service: 'GetRolesForApp',
                parameters: { App: app },
                options: { json: true },
                defaultValue: [],
            });
            return (data || []).map(role => ({
                label: `${app}: ${role.Description || role.Name}`,
                value: `${app}:${role.Name}`,
            }));
        })));
        _cache[cacheKey] = results.flat();
        return _cache[cacheKey];
    }), [cacheKey, uxpContext]);
    const loadOptions = useCallback((max, lastPageToken, args) => __awaiter(void 0, void 0, void 0, function* () {
        const all = yield ensureCache();
        const query = ((args === null || args === void 0 ? void 0 : args.query) || '').toLowerCase();
        const filtered = query ? all.filter(o => o.label.toLowerCase().includes(query)) : all;
        const offset = lastPageToken ? parseInt(lastPageToken, 10) : 0;
        const page = filtered.slice(offset, offset + max);
        const nextToken = offset + max < filtered.length ? String(offset + max) : '';
        return { items: page, pageToken: nextToken };
    }), [ensureCache]);
    const resolveLabels = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        const all = yield ensureCache();
        return values.map(v => {
            var _a;
            const found = all.find(o => o.value === v);
            return { label: (_a = found === null || found === void 0 ? void 0 : found.label) !== null && _a !== void 0 ? _a : v, value: v };
        });
    }), [ensureCache]);
    const selected = value ? value.split(',').map(s => s.trim()).filter(Boolean) : [];
    return (_jsx(MultiSelect, { options: loadOptions, labelField: "label", valueField: "value", selected: selected, onChange: (values) => onChange(values.join(',')), selectedLabels: resolveLabels, placeholder: uxpContext.$L('uxp-core.approle-input.select-app-role'), disabled: disabled, readOnly: readOnly, fullWidth: true }));
};
export default AppRoleInput;
